import { createSelector, createSlice } from "@reduxjs/toolkit";
import { logout } from "../common/reducers/initContext";
import { dataHookFactory, DefaultFailureAction, DefaultSuccessAction } from "../common/reducers/utilities";
import { ensureArray } from "../common/utils/arrayUtilities";
import { markFetched } from "../common/hooks/initHook";

const key = a => a.id;

export const accreditationSlice = createSlice({
    name: 'accreditations',
    initialState: {
        data: {}
    },
    reducers: {
        set: (state, action) => {
            state.data = ensureArray(action.payload).reduce((obj, a) => { obj[key(a)] = a; return obj }, {});
        },
        addOrUpdate: (state, action) => {
            ensureArray(action.payload).forEach(a => state.data[key(a)] = a);
        },
        remove: (state, action) => {
            ensureArray(action.payload).forEach(a => delete state.data[key(a)]);
        }
    },
    extraReducers: builder => {
        builder.addCase(logout, () => accreditationSlice.getInitialState());
    }
});

// #region network operations
const visualizeSuccess = (verb, action) => DefaultSuccessAction(verb, 'accreditation_one', action);
const visualizeFailure = verb => DefaultFailureAction(verb, 'accreditation_one');

const fetchAllAccreditations = network => network.get('/api/v1/accreditations', accreditationSlice.actions.set, visualizeFailure('get'));

export const createAccreditation = (network, accreditation) => network.post('/api/v1/accreditation', accreditation, visualizeSuccess('create', accreditationSlice.actions.addOrUpdate), visualizeFailure('create'));
export const updateAccreditation = (network, delta) => network.put('/api/v1/accreditation', delta, accreditationSlice.actions.addOrUpdate, visualizeFailure('update'));
export const deleteAccreditation = (network, id) => network.delete('/api/v1/accreditation/' + window.encodeURIComponent(id), visualizeSuccess('remove', accreditationSlice.actions.remove), visualizeFailure('remove'));
// #endregion

// #region selectors
const selectAccreditationData = state => state.accreditations.data;
const selectAccreditations = createSelector([selectAccreditationData], data => Object.values(data));
const selectAccreditation = id => createSelector([selectAccreditationData], accreditations => accreditations[id]);
// #endregion

export const canRouteAccreditations = ({credentials}) => credentials.hasRole(['administrator', 'auditor', 'training manager', 'training auditor', 'accreditation auditor'])

const listen = {
    accreditationCreated: (accreditation, { dispatch, websocket }) => {
        markFetched(websocket, 'accreditation', key(accreditation))
        dispatch(accreditationSlice.actions.addOrUpdate(accreditation))
    },
    accreditationUpdated: (accreditation, { dispatch }) => dispatch(accreditationSlice.actions.addOrUpdate(accreditation)),
    accreditationRemoved: (accreditation, { dispatch }) => dispatch(accreditationSlice.actions.remove(accreditation))
}
const initAccreditations = { run: fetchAllAccreditations, listen, immortal: true };
export const [useAccreditations, realizeAccreditations] = dataHookFactory(selectAccreditations, initAccreditations);
export const [useAccreditation] = dataHookFactory(selectAccreditation, id => initAccreditations);

const reducer = { name: accreditationSlice.name, reducer: accreditationSlice.reducer }
export default reducer;